/**
 * Weapon bolts: a fixed pool of glowing projectiles with collision.
 *
 * Every bolt sweeps a segment per frame and tests, in order: registered
 * COLLIDERS (ships, characters, drones — one spherical bound each, rebuilt
 * by the caller every frame) and the analytic TERRAIN of whichever planet
 * it flies over. Hits spawn an impact flash and are queued for the caller
 * to drain — the bolt system reports contacts, the game decides what they
 * mean (drone claims, ship feedback sparks; damage authority lives above).
 * Positions live in system space so floating-origin rebases don't bend
 * shots.
 */
import * as THREE from "three";
import { createShot, stepShot, weaponStats, ITEMS, waterGravity, type BallisticShot, type ShotVector, type ShotTarget, cliffHit, seaSurface, surfaceBoundaryRadius } from '@hyperlight/shared';
import type { Planet } from "../world/planet";

const POOL = 96;
const DEFAULT_SPEED = 700; // ship cannons; on-foot weapons pass their own
const LIFETIME = 1.4;
const IMPACT_POOL = 24;
const IMPACT_LIFE = 0.32;
/** Terrain/prop impacts: a DUST puff, deliberately unlike the bolt. The old warm
 *  additive bloom at the death point read as a SECOND bolt spawning (user report
 *  2026-08-14, night footage) — same color, same glow, appearing right as the
 *  tracer vanished. Dust is normal-blended (no glow), dimmer, and shorter. */
const DUST_LIFE = 0.22;

export type ColliderKind = "ship" | "char" | "drone";

export interface Collider {
  kind: ColliderKind;
  id: number;
  pos: THREE.Vector3; // system space
  radius: number;
}

export interface BoltHit {
  kind: ColliderKind | "terrain";
  id: number; // collider id; -1 for terrain
  point: [number, number, number]; // system space
  /** The bolt's normalized travel direction at contact (victim-reaction orientation). */
  dir?: [number, number, number];
  /** The weapon item id that fired this bolt (rides into the PvE `dhit` claim). */
  wp?: string;
  damage: number;
  /** True if the local player fired the bolt (hit claims need this). */
  mine: boolean;
  /** True when the SERVER owns this bolt's damage (server-authoritative shot). The client
   *  renders it as a predicted VISUAL only — no local claim, no local damage number; the
   *  authoritative `bhit` drives the damage. */
  predicted: boolean;
}

interface Bolt {
  shot: BallisticShot;
  mesh: THREE.Mesh;
  pos: THREE.Vector3; // system space
  vel: THREE.Vector3;
  /** Carry applied by the LAST sim tick (carryBodies). The render draws at
   *  pos − carry·(1−alpha): planets render at an alpha-LERPED pose between
   *  ticks, and a bolt carried in full-tick steps zigzags laterally against
   *  that lerped world by half a tick of carry every other frame at 120 Hz —
   *  which reads as TWO parallel bolts (user report ×2, 2026-08-17; same
   *  phase-mismatch class as the damage-number jitter fix). */
  carry: THREE.Vector3;
  /** Flight distance so far (m) — clamps the drawn streak so the tail can never
   *  extend back past the muzzle it left ("shots must START at the weapon"). */
  traveled: number;
  ttl: number;
  damage: number;
  mine: boolean;
  predicted: boolean;
  /** Weapon item id that fired this bolt (PvE claim attribution). */
  wp?: string;
  /** Collider to skip (the shooter's own bound). */
  ignoreKind: ColliderKind | null;
  ignoreId: number;
}

/** Tracer geometry length (m). The DRAWN streak stretches to cover one frame's
 *  travel: a 2.6 m tracer at 620 m/s moves ~5 m between 120 Hz frames, so
 *  successive images never overlapped and a single bolt strobed as a DASHED
 *  CHAIN of bolts (the second face of the 2026-08-17 "two bolts" report). */
const BOLT_LEN = 2.6;
/** Drawn length on the SPAWN frame (m) — a short stub right at the muzzle. */
const SPAWN_STUB = 0.6;
/** The streak's tail stays PINNED at the muzzle for this much flight (m), then
 *  tapers off to the normal frame-travel length. Without it the tail released
 *  after a single frame (~9 ms), and in tight ADS — where the camera sits
 *  nearly on the fire line — the already-detached tracer converging on the
 *  reticle read as spawning FROM THE CAMERA, not the gun (user 2026-08-17). */
const MUZZLE_ANCHOR = 14;
const boltGeo = new THREE.CylinderGeometry(0.06, 0.06, BOLT_LEN, 5);
boltGeo.rotateX(Math.PI / 2); // align with -Z travel
// Tail-pivoted geometry: local z spans [-BOLT_LEN, 0], so mesh.position is the
// actual bolt origin. With centred geometry the object had to be translated by
// half its drawn length along `dir`; merely turning the player therefore moved
// the renderer's apparent spawn point around the authored muzzle socket.
boltGeo.translate(0, 0, -BOLT_LEN / 2);
const FWD = new THREE.Vector3(0, 0, -1);

export class BoltSystem {
  private bolts: Bolt[] = [];
  private next = 0;
  private mat1: THREE.MeshBasicMaterial;
  private mat2: THREE.MeshBasicMaterial;

  /** Rebuilt by the caller every frame (remote ships, characters, drones). */
  readonly colliders: Collider[] = [];
  private hits: BoltHit[] = [];

  private impacts: { sprite: THREE.Sprite; t: number; life: number; dust: boolean; size: number; pos: THREE.Vector3; carry: THREE.Vector3 }[] = [];
  private nextImpact = 0;
  private flashTex: THREE.CanvasTexture;
  private dustTex: THREE.CanvasTexture;
  private toC = new THREE.Vector3();
  private surfaceDir = new THREE.Vector3();
  private lp0 = new THREE.Vector3();
  private lp1 = new THREE.Vector3();
  private cliffHit = cliffHit();

  constructor(private scene: THREE.Scene) {
    this.mat1 = new THREE.MeshBasicMaterial({ color: 0xffd166 });
    this.mat2 = new THREE.MeshBasicMaterial({ color: 0xff5d8f });
    for (let i = 0; i < POOL; i++) {
      const mesh = new THREE.Mesh(boltGeo, this.mat1);
      mesh.visible = false;
      mesh.frustumCulled = false;
      scene.add(mesh);
      this.bolts.push({
        mesh,
        shot: createShot([0,0,0],[0,0,-1],weaponStats({})),
        pos: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        carry: new THREE.Vector3(),
        traveled: 0,
        ttl: 0,
        damage: 0,
        mine: false,
        predicted: false,
        ignoreKind: null,
        ignoreId: -1,
      });
    }
    this.flashTex = makeFlashTexture();
    this.dustTex = makeDustTexture();
    const impactMat = new THREE.SpriteMaterial({
      map: this.flashTex,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
    });
    for (let i = 0; i < IMPACT_POOL; i++) {
      const sprite = new THREE.Sprite(impactMat.clone());
      sprite.visible = false;
      scene.add(sprite);
      this.impacts.push({ sprite, t: 0, life: IMPACT_LIFE, dust: false, size: 1, pos: new THREE.Vector3(), carry: new THREE.Vector3() });
    }
  }

  /**
   * Spawn one bolt. All vectors in system space; shooterVel is inherited so
   * shots don't lag behind a fast-moving ship.
   */
  private carryTmp = new THREE.Vector3();
  private carryQ2 = new THREE.Quaternion();
  private carryAx = new THREE.Vector3();
  private spawnRotation = new THREE.Matrix4();
  private spawnPhase = new THREE.Matrix3();
  private spawnPoint = new THREE.Vector3();

  /**
   * Bolts ride rotating/orbiting bodies like everything else — without
   * this they visibly drift sideways relative to the ground and targets
   * (Coriolis) the moment a planet's frame carries the combatants. Runs once
   * per FIXED sim tick. Each point records the delta this tick applied
   * (`carry`) so update() can phase the DRAWN position to the render alpha.
   * IMPACT sprites ride too: a dust puff pinned in system space glides ~25 m
   * across a fast-carried world during its short life — a moving dot that
   * reads as another projectile (part of the 2026-08-17 "two bolts" report).
   */
  carryBodies(planets: import("../world/planet").Planet[]): void {
    for (const b of this.bolts) {
      if (b.ttl <= 0) continue;
      this.carryPoint(planets, b.pos, b.vel, b.carry);
    }
    for (const im of this.impacts) {
      if (im.t <= 0) continue;
      this.carryPoint(planets, im.pos, null, im.carry);
    }
  }

  /** Apply one tick of the nearest body's carry to a system-space point (and
   *  optionally a velocity), recording the applied delta in `carryOut`. */
  private carryPoint(
    planets: readonly import("../world/planet").Planet[],
    pos: THREE.Vector3,
    vel: THREE.Vector3 | null,
    carryOut: THREE.Vector3,
  ): void {
    carryOut.set(0, 0, 0);
    this.carryQ2.identity();
    let best: import("../world/planet").Planet | null = null;
    let bestD = Infinity;
    for (const p of planets) {
      const d = pos.distanceTo(p.pose.center) - p.def.radius;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    if (!best) return;
    const R = best.def.radius;
    const k = Math.max(0, Math.min(1, (2.6 * R - (bestD + R)) / (0.6 * R)));
    if (k <= 0) return;
    const px = pos.x, py = pos.y, pz = pos.z;
    const dA = (best.pose.spinAngle - best.posePrev.spinAngle) * k;
    this.carryAx.set(best.def.spin.axis[0], best.def.spin.axis[1], best.def.spin.axis[2]);
    this.carryQ2.setFromAxisAngle(this.carryAx, dA);
    pos.sub(best.posePrev.center).applyQuaternion(this.carryQ2).add(best.posePrev.center);
    if (vel) vel.applyQuaternion(this.carryQ2);
    this.carryTmp.copy(best.pose.center).sub(best.posePrev.center);
    pos.addScaledVector(this.carryTmp, k);
    carryOut.set(pos.x - px, pos.y - py, pos.z - pz);
  }

  spawn(
    muzzleSys: THREE.Vector3,
    dirSys: THREE.Vector3,
    shooterVel: THREE.Vector3,
    group: 1 | 2,
    opts?: {
      speed?: number;
      damage?: number;
      mine?: boolean;
      /** Max travel (m) — converts to TTL at this bolt's speed (default: the pool LIFETIME). */
      range?: number;
      /** Weapon item id that fired this bolt — rides into the PvE hit claim. */
      wp?: string;
      /** Server owns this bolt's damage; render it as a predicted visual only. */
      predicted?: boolean;
      /** Supply when the muzzle comes from an interpolated render pose rather
       *  than the current simulation tick. The caller's vectors stay unchanged. */
      renderFrame?: { planets: readonly Planet[]; alpha: number };
      ignore?: { kind: ColliderKind; id: number };
    },
  ): void {
    const b = this.bolts[this.next];
    this.next = (this.next + 1) % POOL;
    b.pos.copy(muzzleSys);
    b.carry.set(0, 0, 0); // fresh bolt — a reused slot's stale carry would ghost one frame
    b.vel.copy(dirSys).multiplyScalar(opts?.speed ?? DEFAULT_SPEED).add(shooterVel);
    b.ttl = opts?.range !== undefined ? opts.range / (opts?.speed ?? DEFAULT_SPEED) : LIFETIME;
    b.damage = opts?.damage ?? 10;
    b.mine = opts?.mine ?? false;
    b.predicted = opts?.predicted ?? false;
    b.wp = opts?.wp;
    b.ignoreKind = opts?.ignore?.kind ?? null;
    b.ignoreId = opts?.ignore?.id ?? -1;
    b.traveled = 0;
    b.shot=createShot(muzzleSys.toArray() as ShotVector,dirSys.toArray() as ShotVector,
      weaponStats({...ITEMS[opts?.wp??'']?.weapon,projectileSpeed:opts?.speed??DEFAULT_SPEED,
        damage:b.damage,range:opts?.range??(opts?.speed??DEFAULT_SPEED)*LIFETIME}));
    if (opts?.renderFrame) {
      const { planets, alpha } = opts.renderFrame;
      const back = 1 - Math.max(0, Math.min(1, alpha));
      // update() draws x - back * (x - previous(x)). Invert that same
      // affine transform to seed the simulation position from the drawn
      // muzzle. Adding just a translation is insufficient on spinning worlds.
      // Seed carry too: a high-refresh frame may render before another tick.
      this.spawnPoint.copy(muzzleSys);
      this.carryPoint(planets, this.spawnPoint, null, b.carry);
      this.carryQ2.invert();
      this.spawnRotation.makeRotationFromQuaternion(this.carryQ2);
      this.spawnPhase.setFromMatrix4(this.spawnRotation).multiplyScalar(back);
      const e = this.spawnPhase.elements;
      e[0] += 1 - back;
      e[4] += 1 - back;
      e[8] += 1 - back;
      b.carry.applyQuaternion(this.carryQ2).applyMatrix3(this.spawnPhase.invert());
      b.pos.addScaledVector(b.carry, back);
    }
    b.mesh.material = group === 1 ? this.mat1 : this.mat2;
    b.mesh.quaternion.setFromUnitVectors(FWD, dirSys);
    // Draw IMMEDIATELY as a short stub at the muzzle: the local player's shot
    // spawns AFTER this frame's update() pass (the aim solve waits for the
    // finalized camera), so without this the mesh would show one frame at its
    // pool slot's stale position — a one-frame ghost bolt.
    if (this.lastOrigin) {
      b.mesh.scale.set(1, 1, SPAWN_STUB / BOLT_LEN);
      // The geometry origin IS its tail, so this transform is the authored
      // socket exactly—no direction-dependent half-length offset.
      b.mesh.position.copy(muzzleSys).sub(this.lastOrigin);
      b.mesh.visible = true;
    } else {
      b.mesh.visible = false; // no frame rendered yet — update() reveals it
    }
  }

  /** Bolts currently in flight (tests + future tracers read this). */
  get liveCount(): number {
    let n = 0;
    for (const b of this.bolts) if (b.ttl > 0) n++;
    return n;
  }

  /** Contacts since the last drain (the game layer interprets them). */
  drainHits(): BoltHit[] {
    if (this.hits.length === 0) return this.hits;
    const out = this.hits;
    this.hits = [];
    return out;
  }

  private dirTmp = new THREE.Vector3();
  private drawTmp = new THREE.Vector3();
  /** Floating origin of the last update() — lets spawn() place a mesh mid-frame. */
  private lastOrigin: THREE.Vector3 | null = null;

  update(
    dt: number,
    origin: THREE.Vector3,
    planets: readonly Planet[] = [],
    onWaterHit?: (planet: Planet, pointSys: THREE.Vector3) => void,
    /** Camera position in RENDER space (world − origin) — distance-scales the
     *  impact flashes so a hit confirm at rifle range stays readable. */
    eye?: THREE.Vector3,
    /** Render interpolation phase (accumulator / FIXED_DT, same value the
     *  planet/character render poses lerp by). Bolts and impact sprites draw at
     *  pos − carry·(1−alpha) so they sit in the SAME sub-tick phase as the
     *  drawn world — carried to the full tick they strobe laterally against it. */
    alpha = 1,
  ): void {
    const backK = 1 - Math.max(0, Math.min(1, alpha));
    (this.lastOrigin ??= new THREE.Vector3()).copy(origin);
    for (const b of this.bolts) {
      if (b.ttl <= 0) continue;
      b.shot.position=b.pos.toArray() as ShotVector;
      b.shot.velocity=b.vel.toArray() as ShotVector;
      const targets:ShotTarget[]=this.colliders.filter(c=>!(c.kind===b.ignoreKind&&c.id===b.ignoreId))
        .map(c=>({key:`${c.kind}:${c.id}`,position:c.pos.toArray() as ShotVector,radius:c.radius}));
      const result=stepShot(b.shot,dt,{
        targets:()=>targets,solid:(a,z)=>this.solid(a,z,planets),
        gravity:at=>{
          let nearest:Planet|undefined,distance=Infinity;
          for(const p of planets){const d=Math.hypot(at[0]-p.pose.center.x,at[1]-p.pose.center.y,at[2]-p.pose.center.z)-p.def.radius;if(d<distance){nearest=p;distance=d;}}
          if(!nearest)return [0,0,0];
          const d=this.toC.fromArray(at).sub(nearest.pose.center).normalize().multiplyScalar(-waterGravity(nearest.def.radius));
          return d.toArray() as ShotVector;
        },
      });
      b.pos.fromArray(b.shot.position);b.vel.fromArray(b.shot.velocity);b.traveled=b.shot.distance;
      const vl=b.vel.length()||1;
      for(const hit of result.contacts){
        const [kind,id]=hit.target.key.split(':');
        this.hits.push({kind:kind as ColliderKind,id:Number(id),point:hit.point,
          dir:[b.vel.x/vl,b.vel.y/vl,b.vel.z/vl],wp:b.wp,damage:hit.damage,mine:b.mine,predicted:b.predicted});
        this.flash(this.drawTmp.fromArray(hit.point));
      }
      if(result.stop){
        if(b.shot.stats.explosiveDamage>0)this.flash(b.pos,Math.max(1,b.shot.stats.explosiveRadius/3));
        else if(!result.contacts.length){
          this.hits.push({kind:'terrain',id:-1,point:result.stop,wp:b.wp,damage:0,mine:b.mine,predicted:b.predicted});
          let water=false;
          for(const p of planets){const r=this.toC.copy(b.pos).sub(p.pose.center).length();if(p.def.seaRadius&&Math.abs(r-p.def.seaRadius)<2&&seaSurface(p.def)==='water'){onWaterHit?.(p,b.pos);water=true;break;}}
          if(!water)this.dust(b.pos);
        }
        b.ttl=0;b.mesh.visible=false;continue;
      }
      // DRAW: alpha-phased to the render pose, and stretched to cover this
      // frame's travel (head at the sim position, streak trailing) so
      // successive frames overlap into one continuous tracer instead of a
      // strobed chain of short dashes.
      const speed = b.vel.length();

      this.dirTmp.copy(b.vel).divideScalar(speed || 1);
      // Cap keeps a hitching low-fps frame from smearing a tracer across 100+ m;
      // the traveled clamp keeps the tail from reaching back past the muzzle;
      // the MUZZLE_ANCHOR term holds the tail AT the muzzle for the first
      // stretch of flight (streak grows out of the barrel), then tapers.
      const want = Math.min(
        Math.max(BOLT_LEN, Math.min(30, speed * dt * 1.15)),
        Math.max(SPAWN_STUB, b.traveled),
      );
      const anchor = Math.min(b.traveled, MUZZLE_ANCHOR, MUZZLE_ANCHOR * 2 - b.traveled);
      const drawLen = Math.max(want, anchor);
      b.mesh.scale.set(1, 1, drawLen / BOLT_LEN);
      b.mesh.quaternion.setFromUnitVectors(FWD, this.dirTmp); // follow the CARRIED velocity
      this.drawTmp.copy(b.pos).addScaledVector(b.carry, -backK).sub(origin);
      // b.pos is the leading point. Tail-pivoted geometry starts one full draw
      // length behind it and extends forward to the head.
      b.mesh.position.copy(this.drawTmp).addScaledVector(this.dirTmp, -drawLen);
      b.mesh.visible = true;
    }

    // Impact flashes/dust: quick bloom-out.
    for (const im of this.impacts) {
      if (im.t <= 0) continue;
      im.t -= dt;
      if (im.t <= 0) {
        im.sprite.visible = false;
        continue;
      }
      const k = 1 - im.t / im.life;
      im.sprite.position.copy(im.pos).addScaledVector(im.carry, -backK).sub(origin);
      if (im.dust) {
        // Dust: modest growth, gentler distance floor (a long-range landing
        // should stay FINDABLE, not become a beacon), fades early.
        const distK = eye ? Math.max(1, im.sprite.position.distanceTo(eye) / 80) : 1;
        im.sprite.scale.setScalar((0.7 + k * 1.8) * distK);
        (im.sprite.material as THREE.SpriteMaterial).opacity = 0.65 * (1 - k);
      } else {
        // Distance floor (user call 2026-08-10): a fixed 4 m flash is a couple of
        // pixels at rifle range (220–520 m), so the hit confirm vanished exactly
        // when aim feedback matters most. Physical size up close; past ~40 m the
        // flash grows with distance so its SCREEN size stops shrinking.
        const distK = eye ? Math.max(1, im.sprite.position.distanceTo(eye) / 40) : 1;
        im.sprite.scale.setScalar((0.8 + k * 3.2) * distK * im.size);
        (im.sprite.material as THREE.SpriteMaterial).opacity = 1 - k;
      }
    }
  }

  /** Shared sweep adapter: cover is used for direct shots and blast visibility. */
  private solid(from:ShotVector,to:ShotVector,planets:readonly Planet[]):number|null {
    let best:number|null=null;
    const take=(t:number|null|undefined)=>{if(t!=null&&t>=0&&t<=1)best=best===null?t:Math.min(best,t);};
    for(const p of planets){
      this.lp0.fromArray(from).sub(p.pose.center).applyQuaternion(p.pose.invQuat);
      this.lp1.fromArray(to).sub(p.pose.center).applyQuaternion(p.pose.invQuat);
      if(Math.min(this.lp0.length(),this.lp1.length())-this.lp0.distanceTo(this.lp1)>p.def.radius*1.1)continue;
      const a=this.lp0.toArray() as ShotVector,z=this.lp1.toArray() as ShotVector;
      take(p.settlementColliders?.segmentHit(...a,...z));take(p.homesteadSegmentHit?.(...a,...z,0));
      take(p.scatterSegmentHit(...a,...z));take(p.terrainSolids?.segmentHit(a,z,this.cliffHit)?.t);
      for(let k=1;k<=4;k++){
        const t=k/4;this.surfaceDir.lerpVectors(this.lp0,this.lp1,t);const r=this.surfaceDir.length();this.surfaceDir.divideScalar(r||1);
        const ground=surfaceBoundaryRadius(p.sampler,this.surfaceDir.x,this.surfaceDir.y,this.surfaceDir.z);
        if(r<=ground-.35){take(t);break;}
      }
    }
    return best;
  }

  /** Public so the game can flash non-bolt events (drone deaths). */
  flash(posSys: THREE.Vector3, scale = 1): void {
    const im = this.impacts[this.nextImpact];
    this.nextImpact = (this.nextImpact + 1) % IMPACT_POOL;
    im.pos.copy(posSys);
    im.carry.set(0, 0, 0);
    im.t = im.life = IMPACT_LIFE * scale;
    im.dust = false;
    im.size = scale;
    const mat = im.sprite.material as THREE.SpriteMaterial;
    mat.map = this.flashTex;
    mat.blending = THREE.AdditiveBlending;
    im.sprite.visible = true;
    im.sprite.scale.setScalar(0.8 * scale);
  }

  /** MUZZLE flash: a small, brief glow at the barrel tip on firing — anchors
   *  the shot's origin to the WEAPON (in tight ADS the tracer alone reads as
   *  coming from the camera). Deliberately short and small. */
  muzzleFlash(posSys: THREE.Vector3): void {
    const im = this.impacts[this.nextImpact];
    this.nextImpact = (this.nextImpact + 1) % IMPACT_POOL;
    im.pos.copy(posSys);
    im.carry.set(0, 0, 0);
    im.t = im.life = 0.09;
    im.dust = false;
    im.size = 0.32;
    const mat = im.sprite.material as THREE.SpriteMaterial;
    mat.map = this.flashTex;
    mat.blending = THREE.AdditiveBlending;
    im.sprite.scale.setScalar(0.8 * 0.32);
    // Fire happens after BoltSystem.update() so a newly activated pooled sprite
    // will not receive its render-space position until next frame. Showing it
    // at the slot's stale impact/flash position produced the apparent SECOND
    // bolt above the barrel for one frame. Place it immediately, exactly like
    // spawn() does for the predicted tracer stub.
    if (this.lastOrigin) {
      im.sprite.position.copy(posSys).sub(this.lastOrigin);
      im.sprite.visible = true;
    } else {
      im.sprite.visible = false;
    }
  }

  /** Terrain/prop impact: a soft dust puff — visible, but never bolt-like. */
  dust(posSys: THREE.Vector3): void {
    const im = this.impacts[this.nextImpact];
    this.nextImpact = (this.nextImpact + 1) % IMPACT_POOL;
    im.pos.copy(posSys);
    im.carry.set(0, 0, 0);
    im.t = im.life = DUST_LIFE;
    im.dust = true;
    im.size = 1;
    const mat = im.sprite.material as THREE.SpriteMaterial;
    mat.map = this.dustTex;
    mat.blending = THREE.NormalBlending;
    im.sprite.visible = true;
    im.sprite.scale.setScalar(0.7);
  }

  dispose(): void {
    for (const b of this.bolts) this.scene.remove(b.mesh);
    for (const im of this.impacts) {
      this.scene.remove(im.sprite);
      (im.sprite.material as THREE.SpriteMaterial).dispose();
    }
    this.mat1.dispose();
    this.mat2.dispose();
    this.flashTex.dispose();
    this.dustTex.dispose();
  }
}

function makeFlashTexture(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  g.addColorStop(0, "rgba(255, 240, 200, 1)");
  g.addColorStop(0.35, "rgba(255, 190, 120, 0.7)");
  g.addColorStop(1, "rgba(255, 140, 60, 0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

/** Pale, cool dust — normal-blended so it never glows in the dark. A few offset
 *  blobs instead of one radial so it reads as a PUFF, not a point light. */
function makeDustTexture(): THREE.CanvasTexture {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const blob = (x: number, y: number, r: number, a: number): void => {
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(205, 198, 185, ${a})`);
    g.addColorStop(0.6, `rgba(185, 178, 165, ${a * 0.5})`);
    g.addColorStop(1, "rgba(170, 165, 155, 0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
  };
  blob(32, 34, 26, 0.9);
  blob(22, 26, 14, 0.7);
  blob(43, 27, 12, 0.7);
  blob(30, 44, 13, 0.6);
  return new THREE.CanvasTexture(canvas);
}
